"use client";

import { useEffect, useCallback, useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type { SimpleTableConfig } from "../../UniversalCRMTable/types/index";
import { isZodError } from "../types";

interface UseFormValidationProps {
  form: UseFormReturn<FieldValues>;
  config: SimpleTableConfig<any>;
  submissionError: string | null;
  setSubmissionError: (error: string | null) => void;
  setHasValidationErrors: (hasErrors: boolean) => void;
  setShowErrorPopup: (show: boolean) => void;
  setManualErrorInfo: (
    info: {
      count: number;
      fields: Array<{ fieldLabel: string; message: string; fieldName: string }>;
    } | null
  ) => void;
  setErrorFieldNames: (names: Set<string>) => void;
}

/**
 * Handles validation error tracking and extraction
 * Manages validation state and error popup display
 */
export function useFormValidation({
  form,
  config,
  submissionError,
  setSubmissionError,
  setHasValidationErrors,
  setShowErrorPopup,
  setManualErrorInfo,
  setErrorFieldNames,
}: UseFormValidationProps) {
  const { errors } = form.formState;

  // Track validation errors
  useEffect(() => {
    const errorCount = Object.keys(errors).length;
    setHasValidationErrors(errorCount > 0);

    // Clear submission error when validation errors are fixed
    if (errorCount === 0 && submissionError) {
      setSubmissionError(null);
    }
  }, [errors, submissionError, setSubmissionError, setHasValidationErrors]);

  // Handle validation errors when form submission is attempted
  const handleValidationError = useCallback(
    (formErrors: Record<string, unknown>) => {
      const errorCount = Object.keys(formErrors).length;

      if (errorCount > 0) {
        setShowErrorPopup(true);
      }
    },
    [setShowErrorPopup]
  );

  // Get error information for display
  const errorInfo = useMemo(() => {
    const errorEntries = Object.entries(errors);
    const errorCount = errorEntries.length;

    if (errorCount === 0) return null;

    return {
      count: errorCount,
      fields: errorEntries.map(([fieldName, error]) => ({
        fieldName,
        fieldLabel: config.fields[fieldName]?.label || fieldName,
        message: (error?.message as string) || "Invalid value",
      })),
    };
  }, [errors, config.fields]);

  // Extract error fields from validation errors
  const extractErrorFields = useCallback(
    (validationErrors: Record<string, unknown>) => {
      return Object.entries(validationErrors).map(([fieldName, error]) => {
        let message = "This field is required";
        if (error && typeof error === "object") {
          if ("message" in error && typeof error.message === "string") {
            message = error.message;
          }
        }
        return {
          fieldName,
          fieldLabel: config.fields[fieldName]?.label || fieldName,
          message,
        };
      });
    },
    [config.fields]
  );

  // Extract error fields from ZodError
  const extractZodErrorFields = useCallback(
    (error: any) => {
      return (
        error.issues?.map((issue: any) => ({
          fieldName: issue.path?.[0] || "unknown",
          fieldLabel:
            config.fields[issue.path?.[0] || "unknown"]?.label || issue.path?.[0] || "unknown",
          message: issue.message,
        })) || []
      );
    },
    [config.fields]
  );

  // Handle showing validation errors in popup
  const showValidationErrorPopup = useCallback(
    (errorFields: Array<{ fieldLabel: string; message: string; fieldName: string }>) => {
      setManualErrorInfo({
        count: errorFields.length,
        fields: errorFields,
      });
      setErrorFieldNames(new Set(errorFields.map((f) => f.fieldName)));
      setShowErrorPopup(true);
    },
    [setManualErrorInfo, setErrorFieldNames, setShowErrorPopup]
  );

  // Dismiss error popup
  const dismissErrorPopup = useCallback(() => {
    setShowErrorPopup(false);
    setManualErrorInfo(null);
    setErrorFieldNames(new Set());
  }, [setShowErrorPopup, setManualErrorInfo, setErrorFieldNames]);

  return {
    handleValidationError,
    errorInfo,
    extractErrorFields,
    extractZodErrorFields,
    showValidationErrorPopup,
    dismissErrorPopup,
  };
}
